import {
	CalendarEvent,
	EVENT_CATEGORIES,
	EventFilterState,
	EventGroup,
	UNTAGGED,
} from "../types/event";
import { DEFAULT_CATEGORY_IDS } from "./eventFilterStorage";
import { overlapsSchoolDay } from "./schoolDay";

/**
 * The filter rail's facets — what can be ticked, what each tick means, and how
 * many events sit behind it.
 *
 * The rail's design asks for faceted groups with counts. The four groups the
 * design names — Area, School section, Cost, Access — have NO backing field on
 * the S2 event document, so they are not drawn: a tick box that cannot filter
 * is worse than no tick box. These are the facets the event contract actually
 * supports, in the same shape:
 *
 *   Event source   `Category`      — SERVER-side (see below)
 *   Tags           `Tags`          — SERVER-side, the other one
 *   Event type     `EventType`     — the ten values in useEventPublish
 *   Approval       `Approval.*`    — the permission flags and the publish stamp
 *   Visibility     `Groups`        — the audiences the event was built from
 *
 * A LOCATION facet was here too, latterly a select of the venues on screen. It
 * is gone: the rail has room for one control above the fold and Event type is
 * the one people reach for, and a filter matched on a venue's Edumate spelling
 * was never the way anyone found an event. `Location` is still on the document
 * and still shown everywhere an event is — nothing about the data changed.
 *
 * Everything except Event source and Tags is applied CLIENT-side over the events
 * the calendar has already fetched for the visible range, which is also why
 * every count is a count of what is on screen rather than of the collection.
 *
 * THE TWO SERVER-SIDE FACETS ARE NOT THE SAME KIND OF THING, which is the trap
 * here. `categories` is an allow-list: an empty one asks for nothing and the
 * calendar goes blank. `tags` NARROWS a result the API would otherwise return
 * whole, so an empty one means no constraint, like every client-side facet. The
 * consequence for counting is spelled out on the tag facet below.
 */

/**
 * Whether the sign-off process has RELEASED this event.
 *
 * `published` and nothing else. An event with no `PublishWorkflow` was never
 * submitted, so it is not released — which is what the API's `unapproved`
 * filter means by the word and what the sign-off screen's "Not approved only"
 * shows.
 *
 * True of MOST events on this calendar, which is why nothing asks this on its
 * own: see isOwnEvent.
 *
 * Exported as the app's one definition of the word rather than because
 * something else needs it yet: `matchesFilters` below is the only caller, and
 * anything that later wants to mark an unreleased event on screen should ask
 * here rather than re-test the status itself.
 */
export const isReleased = (evt: CalendarEvent): boolean =>
	evt.PublishWorkflow?.Status === "published";

/**
 * Created HERE, rather than arriving from Edumate or Clipboard.
 *
 * The gate on the approval filter, and the reason that filter is useful rather
 * than destructive. Only these events have a sign-off chain: an Edumate lesson
 * or a Clipboard booking is never submitted for release, so `isReleased` is
 * false for every one of them and hiding on that alone would take the whole
 * timetable off the calendar. Asked together, the pair means "an event we made
 * that is not out yet", which is the question worth asking.
 *
 * READ OFF `ForeignKeys`, NOT `Source`. `Source` is the calendar LABEL, and an
 * event created here keeps whichever calendar was picked in the create dialog —
 * Edumate and Clipboard are both offered — so one filed under Edumate arrived
 * as `Source: "edumate"` and was never hidden, however far into sign-off it
 * was. The sync stamps its own key and the create path writes none, which is
 * the same evidence `eventOrigin` uses. A workflow of any status settles it
 * outright: whatever is in sign-off has a sign-off chain.
 */
export const isOwnEvent = (evt: CalendarEvent): boolean => {
	if (evt.PublishWorkflow?.Status) return true;
	const keys = evt.ForeignKeys ?? {};
	return !keys.Edumate && !keys.Clipboard && !keys.LISS;
};

/** A facet whose selection is applied here rather than by the API. */
export type FacetKey =
	"searchText" | "eventType" | "groups";

export interface FacetOptions {
	/**
	 * Every tag anyone has used, from `useEventTags`.
	 *
	 * The tag facet CANNOT list the tags on screen the way the type and group
	 * facets do. Ticking one filters at the API, so the response then contains
	 * only events carrying it, and every other tag would disappear from the rail
	 * the moment it was used — leaving no way to widen the selection. The
	 * catalogue is the stable list, so it is the one drawn.
	 */
	tagCatalogue?: string[];
	/**
	 * The API has already applied `searchText`, so this file must not apply it
	 * again.
	 *
	 * Not an optimisation. The endpoint matches the term against `Title` OR
	 * `Description.Short`; the pass below only ever looked at the title. Left on
	 * over server-side results, it would fetch every description match and then
	 * hide it, which is a narrower answer than either layer intends and one no
	 * count on screen would explain.
	 */
	searchApplied?: boolean;
}

export interface FacetOption {
	id: string;
	label: string;
	count: number;
	/** Only the source facet has one; it matches the calendar's chip colour. */
	colour?: string;
	/**
	 * False when the count cannot be known — an unticked SOURCE was never
	 * fetched, so the honest answer is no number rather than a zero that reads
	 * as "there are none".
	 */
	counted: boolean;
}

/** Events with no `EventType` are a real group, and a large one. */
export const NO_EVENT_TYPE = "__none";


/**
 * The audience groups on one event, deduplicated.
 *
 * `Groups` is the expanded form and the only one carrying a name; `SelectedGroups`
 * is normally a list of bare ids, but this app wrote whole objects into it for a
 * while, so object entries there are read as a fallback. Bare ids are skipped —
 * a row labelled with a Mongo id is not a filter anyone can use.
 */
export function groupsOf(evt: CalendarEvent): { id: string; label: string }[] {
	const out: { id: string; label: string }[] = [];
	const seen = new Set<string>();
	const push = (g: EventGroup) => {
		const id = g._id ?? g.id;
		const label = g.Label ?? g.label;
		if (!id || !label || seen.has(id)) return;
		seen.add(id);
		out.push({ id, label });
	};

	for (const g of evt.Groups ?? []) push(g);
	for (const g of evt.SelectedGroups ?? []) {
		if (typeof g !== "string") push(g);
	}
	return out;
}

/** The event's type as a facet id — never an empty string, which cannot be ticked. */
const typeIdOf = (evt: CalendarEvent): string =>
	(evt.EventType ?? "").trim() || NO_EVENT_TYPE;

/**
 * Does this event survive the filters?
 *
 * `except` drops ONE facet from the test, which is how each facet's counts are
 * worked out: a count beside an unticked option has to say what you would get
 * by ticking it, so it is taken over the events every OTHER facet allows. Left
 * in, ticking one option would immediately show every other option in its own
 * group as zero.
 *
 * Selections are OR within a facet and AND across facets, the usual convention
 * and the one the source toggles already followed: ticking a second event type
 * shows more, ticking a group as well as a type shows fewer. Note that means
 * ticking two APPROVAL rows widens rather than narrows — "required or medical",
 * not "required and medical".
 *
 * `categories` is absent on purpose. It is a request parameter, so an unticked
 * source is not in `events` to be filtered out. `tags` is absent for the same
 * reason: the API has already applied it, and re-applying it here would be a
 * second, subtly different rule — the server matches case-insensitively, so a
 * pass over `evt.Tags` with `includes` would hide an event stored as "Sport"
 * from a filter ticked as "sport" that the API had correctly returned.
 */
export function matchesFilters(
	evt: CalendarEvent,
	filters: EventFilterState,
	except?: FacetKey,
	opts?: FacetOptions,
): boolean {
	/* Both halves, and the order matters for what it costs: `isOwnEvent` is a
	   string compare that rules out most of the calendar before the status is
	   looked at at all.

	   No `except` branch, because this is not a facet: it has no options to
	   count, so there is no tally that should be taken without it. The facet
	   counts either side of it are counts of what is on screen, which is what
	   they claim to be — including the source counts, so ticking this shows My
	   Pymble's number falling while Edumate's and Clipboard's hold. */
	if (filters.hideUnapproved && isOwnEvent(evt) && !isReleased(evt)) {
		return false;
	}

	/* Guarded rather than unconditional, because unlike every other test here
	   this one parses dates: two Date objects per event, over a month that can
	   hold several thousand, re-run for each facet's tally. Off, it costs a
	   boolean. */
	if (filters.schoolDayOnly && !overlapsSchoolDay(evt.start, evt.end)) {
		return false;
	}

	if (filters.permissionOnly && evt.Approval?.Required !== true) {
		return false;
	}

	if (except !== "searchText" && !opts?.searchApplied) {
		const search = filters.searchText.trim().toLowerCase();
		if (search && !(evt.title ?? "").toLowerCase().includes(search)) {
			return false;
		}
	}

	if (except !== "eventType" && filters.eventType) {
		if (typeIdOf(evt) !== filters.eventType) return false;
	}

	if (except !== "groups" && filters.groups.length > 0) {
		const ids = groupsOf(evt).map((g) => g.id);
		if (!ids.some((id) => filters.groups.includes(id))) return false;
	}

	return true;
}

/** Same ids, order-insensitively — the rail's order is not meaningful. */
export const sameSelection = (a: string[], b: string[]): boolean =>
	a.length === b.length && [...a].sort().join() === [...b].sort().join();

export interface RailFacets {
	sources: FacetOption[];
	eventTypes: FacetOption[];
	groups: FacetOption[];
	tags: FacetOption[];
	/** How many separate choices are narrowing the calendar right now. */
	appliedCount: number;
}

/** Tally `key` over the events every other facet allows. */
function subsetFor(
	events: CalendarEvent[],
	filters: EventFilterState,
	key: FacetKey,
	opts?: FacetOptions,
): CalendarEvent[] {
	return events.filter((evt) => matchesFilters(evt, filters, key, opts));
}

/**
 * Everything the rail draws, worked out in one pass per facet.
 *
 * Options with a zero count are kept when they are TICKED — dropping the row
 * you just ticked would leave a filter running with nothing on screen to untick
 * it — and dropped otherwise, which is what stops the type facet listing all
 * ten values when a week only holds two of them.
 */
export function buildFacets(
	events: CalendarEvent[],
	filters: EventFilterState,
	opts?: FacetOptions,
): RailFacets {
	const tally = (
		subset: CalendarEvent[],
		idsOf: (evt: CalendarEvent) => string[],
	): Map<string, number> => {
		const counts = new Map<string, number>();
		for (const evt of subset) {
			for (const id of idsOf(evt)) {
				counts.set(id, (counts.get(id) ?? 0) + 1);
			}
		}
		return counts;
	};

	// Sources. Counted over everything else's survivors, but only where a count
	// exists to give: an unticked source was never asked for.
	const sourceCounts = tally(
		events.filter((evt) => matchesFilters(evt, filters, undefined, opts)),
		(evt) => [evt.Source],
	);
	const sources: FacetOption[] = EVENT_CATEGORIES.map((cat) => ({
		id: cat.id,
		label: cat.label,
		colour: cat.color,
		count: sourceCounts.get(cat.id) ?? 0,
		counted: filters.categories.includes(cat.id),
	}));

	const typeCounts = tally(subsetFor(events, filters, "eventType", opts), (evt) => [
		typeIdOf(evt),
	]);
	/* The SELECTED type is unioned in even when the range holds none of it.
	   Without it the select would fall back to its placeholder — reading "All
	   types" while one type was filtering the calendar — and there would be no
	   option to choose your way out of. */
	const typeIds = new Set([
		...typeCounts.keys(),
		...(filters.eventType ? [filters.eventType] : []),
	]);
	const eventTypes: FacetOption[] = [...typeIds]
		.map((id) => ({
			id,
			label: id === NO_EVENT_TYPE ? "Not set" : id,
			count: typeCounts.get(id) ?? 0,
			counted: true,
		}))
		// "Not set" last: it is a gap in the data, not a kind of event.
		.sort((a, b) =>
			a.id === NO_EVENT_TYPE
				? 1
				: b.id === NO_EVENT_TYPE
					? -1
					: a.label.localeCompare(b.label),
		);

	const groupCounts = tally(subsetFor(events, filters, "groups", opts), (evt) =>
		groupsOf(evt).map((g) => g.id),
	);
	const groupLabels = new Map<string, string>();
	for (const evt of events) {
		for (const g of groupsOf(evt)) groupLabels.set(g.id, g.label);
	}
	const groupIds = new Set([...groupCounts.keys(), ...filters.groups]);
	const groups: FacetOption[] = [...groupIds]
		.map((id) => ({
			id,
			label: groupLabels.get(id) ?? "Unnamed group",
			count: groupCounts.get(id) ?? 0,
			counted: true,
		}))
		// Biggest audience first, as the design shows; ties by name.
		.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

	/* TAGS. Options from the catalogue, not from the events on screen — see
	   `tagCatalogue` above on why the list has to be stable.

	   NOT COUNTED. The tag filter shows names only: once a tag is ticked the API
	   returns only events carrying it, so every other tag's count is unknowable,
	   and numbers that came and went as the filter changed read as noise. So no
	   tally is taken — it would be a pass over every event in range for a
	   number nothing draws. */
	const catalogue = opts?.tagCatalogue ?? [];
	/* A ticked tag that has since left the catalogue still needs its row, or the
	   filter runs with nothing on screen to turn it off. `__none` is excluded:
	   there is no "Untagged" row (see UNTAGGED), and a stray one here would
	   appear as a tag literally named "__none". */
	const tagIds = [
		...catalogue,
		...filters.tags.filter(
			(t) =>
				t !== UNTAGGED &&
				!catalogue.some(
					(c) => c.localeCompare(t, "en-AU", { sensitivity: "accent" }) === 0,
				),
		),
	];
	const tags: FacetOption[] = tagIds
		.map((id) => ({
			id,
			label: id,
			count: 0,
			counted: false,
		}))
		/* EVERY catalogue tag, including those with no events in range. Dropping
		   the empty ones cut the list down to the tags already on screen, so the
		   filter could only ever find what the person was looking at. A tag that
		   exists but is not used this week is still a tag somebody may want to
		   filter by as they page on. */
		.sort((a, b) => a.label.localeCompare(b.label, "en-AU"));

	const appliedCount =
		(filters.searchText.trim() ? 1 : 0) +
		(filters.hideUnapproved ? 1 : 0) +
		(filters.schoolDayOnly ? 1 : 0) +
		(filters.permissionOnly ? 1 : 0) +
		(filters.eventType ? 1 : 0) +
		filters.groups.length +
		filters.tags.length +
		// The sources only count as a filter once they differ from what a new
		// person sees; otherwise the rail would open claiming two filters that
		// nobody chose.
		(sameSelection(filters.categories, DEFAULT_CATEGORY_IDS) ? 0 : 1);

	return { sources, eventTypes, groups, tags, appliedCount };
}
